import { useCallback, useEffect } from "react";

import useChatSocket from "@/components/chat/useChatSocket";
import type { NotificationsModels } from "@/data/notifications/notifications.models";
import { useNotificationStore } from "@/providers/notifications/notifications.store";
import { useOverlayQueueStore } from "@/providers/overlay/overlay-queue.store";
import { useWelcomeRewardModalStore } from "@/providers/wallet/wallet.store";
import type { RewardUnlockedMetadata } from "@/types/loyalty.types";
import { logger } from "@/util/logger";

const isEndorsementNotification = (type: string) => {
  return type.toLowerCase().includes("endorsement");
};

const isCashbackNotification = (type: string, data: NotificationsModels.NotificationDataDto) => {
  return type === "CashbackAvailableNotification" && data.category === "wallet" && data.type === "cashback_available";
};

const isRewardUnlockedNotification = (type: string, data: NotificationsModels.NotificationDataDto) => {
  return type === "RewardUnlockedNotification" && data.category === "rewards" && data.type === "reward_unlocked";
};

export const NotificationSocketSubscriber = () => {
  const { chatSocket } = useChatSocket();
  const { addNotification } = useNotificationStore();
  const { setShowWelcomeRewardModal } = useWelcomeRewardModalStore();
  const { enqueueOverlay } = useOverlayQueueStore();

  const subscribeToWebsocketEvents = useCallback(() => {
    if (!chatSocket) {
      return;
    }

    logger.info("[NotificationSocketSubscriber] Subscribing to notification events");

    chatSocket.on("notification", (data: any) => {
      logger.info("[NotificationSocketSubscriber] Received notification", data);

      const newNotification = {
        createdAt: data.data.createdAt,
        data: data.data.data,
        id: data.data.id,
        notifiableId: data.data.notifiableId,
        notifiableType: data.data.notifiableType,
        type: data.data.type,
        updatedAt: data.data.updatedAt,
        readAt: data.data.readAt,
      } satisfies NotificationsModels.NotificationDto;

      // Skip endorsement notifications
      if (isEndorsementNotification(newNotification.type)) {
        return;
      }

      // Handle cashback notifications - show welcome reward modal
      if (isCashbackNotification(newNotification.type, newNotification.data)) {
        setShowWelcomeRewardModal(true);
      }

      // Handle reward unlocked notifications - enqueue loyalty modal
      if (isRewardUnlockedNotification(newNotification.type, newNotification.data)) {
        const metadata = newNotification.data.metadata as RewardUnlockedMetadata | undefined;
        logger.info("[NotificationSocketSubscriber] Reward unlocked", metadata);
        if (metadata?.rewardId) {
          enqueueOverlay({
            id: `loyalty-${metadata.rewardId}`,
            type: "loyalty-reward",
            priority: 2, // Lower priority than recalculation modal
            data: {
              id: metadata.rewardId,
              name: metadata.rewardName,
              description: metadata.rewardDescription,
              unlockedAt: metadata.unlockedAt,
              redeemCode: metadata.redeemCode,
              redeemLink: metadata.redeemLink,
              imageUrl: metadata.imageUrl,
              unlockedImageUrl: metadata.unlockedImageUrl,
            },
          });
        }
      }

      // Add the new notification to the store
      addNotification(newNotification);
    });
  }, [chatSocket, addNotification, setShowWelcomeRewardModal, enqueueOverlay]);

  const unsubscribeFromSocketEvents = useCallback(() => {
    if (chatSocket) {
      logger.info("[NotificationSocketSubscriber] Unsubscribing from notification events");
      chatSocket.off("notification");
    }
  }, [chatSocket]);

  useEffect(() => {
    subscribeToWebsocketEvents();
    return () => {
      unsubscribeFromSocketEvents();
    };
  }, [subscribeToWebsocketEvents, unsubscribeFromSocketEvents]);

  // This component doesn't render anything
  return null;
};
